import { Button } from "@/components/ui/button";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  submitSalesIssueReport,
  type SalesIssueSurface,
} from "@/lib/salesSafeIssueReport";
import { useEffect, useRef, useState } from "react";

type ReportState = "idle" | "pending" | "success" | "failure";

type Props = {
  surface: SalesIssueSurface;
  safeErrorCode: string;
  priorDataRetained: boolean;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
  variant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive";
  allowlistedProposalRef?: string | null;
  allowlistedInvoiceRefs?: readonly string[] | null;
  allowlistedOutstandingSgd?: number | null;
};

/**
 * Reusable Sales Report Issue control. Posts allowlisted diagnostics to /api/issues.
 * Never surfaces raw server errors. Pending disables repeated clicks; failure keeps Retry available elsewhere.
 */
export default function ReportIssueButton({
  surface,
  safeErrorCode,
  priorDataRetained,
  className,
  size = "sm",
  variant = "outline",
  allowlistedProposalRef,
  allowlistedInvoiceRefs,
  allowlistedOutstandingSgd,
}: Props) {
  const { user } = useAuth();
  const [state, setState] = useState<ReportState>("idle");
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // Reset durable success when the underlying failure context changes.
  useEffect(() => {
    setState("idle");
  }, [surface, safeErrorCode, priorDataRetained]);

  const label =
    state === "pending"
      ? "Reporting…"
      : state === "success"
        ? "Issue reported"
        : state === "failure"
          ? "Could not report — try again"
          : "Report Issue";

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={className}
      disabled={state === "pending" || state === "success"}
      aria-label={label}
      aria-live="polite"
      data-testid={`report-issue-${surface}`}
      onClick={() => {
        if (state === "pending" || state === "success") return;
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        setState("pending");
        void submitSalesIssueReport({
          surface,
          safeErrorCode,
          priorDataRetained,
          userName: user?.name ?? user?.email ?? null,
          pathname: typeof window !== "undefined" ? window.location.pathname : undefined,
          signal: ac.signal,
          allowlistedProposalRef,
          allowlistedInvoiceRefs,
          allowlistedOutstandingSgd,
        }).then((res) => {
          if (ac.signal.aborted) return;
          setState(res.ok ? "success" : "failure");
        });
      }}
    >
      {label}
    </Button>
  );
}
